import { CSSProperties, HTMLAttributes } from 'react'

import { Direction } from '@react-types/shared'
import { useLocale } from '@react-aria/i18n'

import {
  BackgroundColorValue,
  BorderColorValue,
  BorderRadiusValue,
  BorderSizeValue,
  BorderValue,
  ColorValue,
  DimensionValue,
  StyleProps,
  ViewStyleProps,
} from '../types'

type StyleName = string | string[] | ((dir: Direction) => string)
type StyleHandler = (value: any) => string
export interface StyleHandlers {
  [key: string]: [StyleName, StyleHandler]
}

export const baseStyleProps: StyleHandlers = {
  margin: ['margin', dimensionValue],
  marginStart: [rtl('marginLeft', 'marginRight'), dimensionValue],
  marginEnd: [rtl('marginRight', 'marginLeft'), dimensionValue],
  // marginLeft: ['marginLeft', dimensionValue],
  // marginRight: ['marginRight', dimensionValue],
  marginTop: ['marginTop', dimensionValue],
  marginBottom: ['marginBottom', dimensionValue],
  marginX: [['marginLeft', 'marginRight'], dimensionValue],
  marginY: [['marginTop', 'marginBottom'], dimensionValue],
  width: ['width', dimensionValue],
  height: ['height', dimensionValue],
  minWidth: ['minWidth', dimensionValue],
  minHeight: ['minHeight', dimensionValue],
  maxWidth: ['maxWidth', dimensionValue],
  maxHeight: ['maxHeight', dimensionValue],
  // @ts-ignore
  isHidden: ['display', hiddenValue],
  alignSelf: ['alignSelf', passthroughStyle],
  justifySelf: ['justifySelf', passthroughStyle],
  position: ['position', anyValue],
  zIndex: ['zIndex', anyValue],
  top: ['top', dimensionValue],
  bottom: ['bottom', dimensionValue],
  start: [rtl('left', 'right'), dimensionValue],
  end: [rtl('right', 'left'), dimensionValue],
  left: ['left', dimensionValue],
  right: ['right', dimensionValue],
  // @ts-ignore
  flex: ['flex', flexValue],
  flexGrow: ['flexGrow', passthroughStyle],
  flexShrink: ['flexShrink', passthroughStyle],
  flexBasis: ['flexBasis', passthroughStyle],
  gridArea: ['gridArea', passthroughStyle],
  gridColumn: ['gridColumn', passthroughStyle],
  gridColumnEnd: ['gridColumnEnd', passthroughStyle],
  gridColumnStart: ['gridColumnStart', passthroughStyle],
  gridRow: ['gridRow', passthroughStyle],
  gridRowEnd: ['gridRowEnd', passthroughStyle],
  gridRowStart: ['gridRowStart', passthroughStyle],

  boxSizing: ['boxSizing', passthroughStyle],
}

export const viewStyleProps: StyleHandlers = {
  ...baseStyleProps,
  backgroundColor: ['backgroundColor', backgroundColorValue],
  background: ['background', backgroundColorValue],

  borderWidth: ['borderWidth', borderSizeValue],
  borderStartWidth: [
    rtl('borderLeftWidth', 'borderRightWidth'),
    borderSizeValue,
  ],
  borderEndWidth: [rtl('borderRightWidth', 'borderLeftWidth'), borderSizeValue],
  borderLeftWidth: ['borderLeftWidth', borderSizeValue],
  borderRightWidth: ['borderRightWidth', borderSizeValue],
  borderTopWidth: ['borderTopWidth', borderSizeValue],
  borderBottomWidth: ['borderBottomWidth', borderSizeValue],
  borderXWidth: [['borderLeftWidth', 'borderRightWidth'], borderSizeValue],
  borderYWidth: [['borderTopWidth', 'borderBottomWidth'], borderSizeValue],
  borderColor: ['borderColor', borderColorValue],
  borderStartColor: [
    rtl('borderLeftColor', 'borderRightColor'),
    borderColorValue,
  ],
  borderEndColor: [
    rtl('borderRightColor', 'borderLeftColor'),
    borderColorValue,
  ],
  borderLeftColor: ['borderLeftColor', borderColorValue],
  borderRightColor: ['borderRightColor', borderColorValue],
  borderTopColor: ['borderTopColor', borderColorValue],
  borderBottomColor: ['borderBottomColor', borderColorValue],
  borderXColor: [['borderLeftColor', 'borderRightColor'], borderColorValue],
  borderYColor: [['borderTopColor', 'borderBottomColor'], borderColorValue],
  borderRadius: ['borderRadius', borderRadiusValue],
  borderTopStartRadius: [
    rtl('borderTopLeftRadius', 'borderTopRightRadius'),
    borderRadiusValue,
  ],
  borderTopEndRadius: [
    rtl('borderTopRightRadius', 'borderTopLeftRadius'),
    borderRadiusValue,
  ],
  borderBottomStartRadius: [
    rtl('borderBottomLeftRadius', 'borderBottomRightRadius'),
    borderRadiusValue,
  ],
  borderBottomEndRadius: [
    rtl('borderBottomRightRadius', 'borderBottomLeftRadius'),
    borderRadiusValue,
  ],
  borderTopLeftRadius: ['borderTopLeftRadius', borderRadiusValue],
  borderTopRightRadius: ['borderTopRightRadius', borderRadiusValue],
  borderBottomLeftRadius: ['borderBottomLeftRadius', borderRadiusValue],
  borderBottomRightRadius: ['borderBottomRightRadius', borderRadiusValue],
  color: ['color', colorValue],
  padding: ['padding', dimensionValue],
  paddingStart: [rtl('paddingLeft', 'paddingRight'), dimensionValue],
  paddingEnd: [rtl('paddingRight', 'paddingLeft'), dimensionValue],
  paddingLeft: ['paddingLeft', dimensionValue],
  paddingRight: ['paddingRight', dimensionValue],
  paddingTop: ['paddingTop', dimensionValue],
  paddingBottom: ['paddingBottom', dimensionValue],
  paddingX: [['paddingLeft', 'paddingRight'], dimensionValue],
  paddingY: [['paddingTop', 'paddingBottom'], dimensionValue],
  overflow: ['overflow', passthroughStyle],

  borderStyle: ['borderStyle', passthroughStyle],
  userSelect: ['userSelect', passthroughStyle],
}

export const flexStyleProps: StyleHandlers = {
  ...viewStyleProps,
  // TODO: flesh out
  alignItems: ['alignItems', passthroughStyle],
  display: ['display', passthroughStyle],
  flexDirection: ['flexDirection', flexDirectionValue],
  gap: ['gap', dimensionValue],
  justifyContent: ['justifyContent', passthroughStyle],
}

export const fontStyleProps: StyleHandlers = {
  ...viewStyleProps,
  fontFamily: ['fontFamily', fontFamilyValue],
  fontSize: ['fontSize', fontSizeValue],
  fontStyle: ['fontStyle', passthroughStyle],
  fontWeight: ['fontWeight', passthroughStyle],
  letterSpacing: ['letterSpacing', passthroughStyle],
  textAlign: ['textAlign', passthroughStyle],
  textTransform: ['textTransform', passthroughStyle],
}

const borderStyleProps = {
  borderWidth: 'borderStyle',
  borderLeftWidth: 'borderLeftStyle',
  borderRightWidth: 'borderRightStyle',
  borderTopWidth: 'borderTopStyle',
  borderBottomWidth: 'borderBottomStyle',
}

type FlexDirection = 'row' | 'column'
function flexDirectionValue(value: FlexDirection) {
  return value
}

function rtl(ltr: string, rtl: string) {
  return (direction: Direction) => (direction === 'rtl' ? rtl : ltr)
}

const UNIT_RE = /(%|px|em|rem|vw|vh|auto|cm|mm|in|pt|pc|ex|ch|rem|vmin|vmax|fr)$/
export function dimensionValue(value: DimensionValue) {
  if (typeof value === 'number') {
    return value + 'px'
  }

  if (UNIT_RE.test(value)) {
    return value
  }

  return `var(--dna-dimension-${value}, var(--dna-alias-dimension-size-${value}, var(--dna-alias-${value})))`
}

type DimensionFontValue =
  | 'font-25'
  | 'font-50'
  | 'font-75'
  | 'font-100'
  | 'font-200'
  | 'font-300'
  | 'font-400'
  | 'font-500'
  | 'font-600'
  | 'default'

function fontSizeValue(value: DimensionFontValue) {
  return (
    `var(--dna-dimension-${value}, ` +
    `var(--dna-alias-dimension-font-${value}))`
  )
}

type FontFamilyValue = 'base' | 'mono' | 'default'

function fontFamilyValue(value: FontFamilyValue) {
  return (
    `var(--dna-font-family-${value}, ` +
    `var(--dna-alias-font-family-${value}))`
  )
}

/**
 *
 * Color
 *
 * */
function colorValue(value: ColorValue) {
  return (
    `var(--dna-color-${value}, ` +
    `var(--dna-semantic-color-${value}, ` +
    `var(--dna-alias-color-${value})))`
  )
}

function backgroundColorValue(value: BackgroundColorValue) {
  return `var(--dna-alias-color-background-${value}, ${colorValue(
    value as ColorValue
  )})`
}

function borderColorValue(value: BorderColorValue) {
  return `var(--dna-alias-color-border-${value}, ${colorValue(
    value as ColorValue
  )})`
}

function borderValue(value: BorderValue) {
  return (
    `var(--dna-dimension-${value}, ` +
    `var(--dna-dimension-border-${value}, ` +
    `var(--dna-alias-dimension-border-${value})))`
  )
}

function borderSizeValue(value: BorderSizeValue) {
  return borderValue(`size-${value}` as BorderValue)
}

function borderRadiusValue(value: BorderRadiusValue) {
  return borderValue(`radius-${value}` as BorderValue)
}

function hiddenValue(value: boolean) {
  return value ? 'none' : undefined
}

function anyValue(value: any) {
  return value
}

function flexValue(value: boolean | number | string) {
  if (typeof value === 'boolean') {
    return value ? '1' : undefined
  }

  return '' + value
}

export function convertStyleProps(
  props: ViewStyleProps,
  handlers: StyleHandlers,
  direction: Direction
) {
  let style: CSSProperties = {}

  for (let key in props) {
    let styleProp = handlers[key]
    // @ts-ignore
    if (!styleProp || props[key] == null) {
      continue
    }

    let [name, convert] = styleProp
    if (typeof name === 'function') {
      name = name(direction)
    }

    // ...

    // @ts-ignore
    let value = convert(props[key])
    if (Array.isArray(name)) {
      for (let k of name) {
        // @ts-ignore
        style[k] = value
      }
    } else {
      // @ts-ignore
      style[name] = value
    }
  }

  for (let prop in borderStyleProps) {
    // @ts-ignore
    if (style[prop]) {
      // @ts-ignore
      style[borderStyleProps[prop]] = 'solid'
      style.boxSizing = 'border-box'
    }
  }

  return style
}

export function useStyleProps<T extends StyleProps>(
  props: T,
  handlers: StyleHandlers = baseStyleProps
) {
  // @ts-ignore
  let { UNSAFE_className, UNSAFE_style, ...otherProps } = props
  let { direction } = useLocale()
  let styles = convertStyleProps(props, handlers, direction)
  let style = { ...UNSAFE_style, ...styles }

  // @ts-ignore
  if (otherProps.className)
    console.warn('The className prop is unsafe and is unsupported in DNA.')

  // @ts-ignore
  if (otherProps.style)
    console.warn('The style prop is unsafe and is unsupported in DNA.')

  let styleProps: HTMLAttributes<HTMLElement> = {
    style,
    className: UNSAFE_className,
  }

  if (props.isHidden) {
    styleProps.hidden = true
  }

  return {
    styleProps,
  }
}

export function passthroughStyle(value: any) {
  return value
}
